import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginScreen } from './login/LoginScreen';
import { AppShell } from './shell/AppShell';
import { AffilmasterShell } from './affilmaster/AffilmasterShell';
import { AffilplatformShell } from './affilplatform/AffilplatformShell';
import { DmwkShell } from './dmwk/DmwkShell';
import { SpacesLanding } from './spaces/SpacesLanding';
import { NoActiveSpace } from './shell/NoActiveSpace';
import { PlatformShell } from './shell/PlatformShell';
import { isReservedHostLabel } from './shell/reserved-hosts';
import { taskIdFromUrl } from './shell/task-deep-link';
import { TaskDeepLinkRedirect } from './shell/TaskDeepLinkRedirect';

const EsignSignPage = lazy(() => import('./esign/EsignSignPage').then((m) => ({ default: m.EsignSignPage })));

const ESIGN_SIGN_PATH = /^\/podpis\/([^/]+)\/?$/;

const SHELLS_WITH_UI = new Set<string>(['rondo', 'affilmaster', 'affilplatform', 'dmwk']);

export function App() {
  const accessKey = ESIGN_SIGN_PATH.exec(window.location.pathname)?.[1];
  if (accessKey) {
    return (
      <Suspense fallback={<main className="loading-screen"><p>Načítám…</p></main>}>
        <EsignSignPage accessKey={accessKey} />
      </Suspense>
    );
  }
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}

function Root() {
  const { status, me } = useAuth();
  if (status === 'loading') {
    return (
      <main className="loading-screen">
        <p>Loading…</p>
      </main>
    );
  }
  if (status === 'unknown-space') {
    // Dev-only fail-loud screen - the host points at a Space subdomain that
    // doesn't exist in this tenant. Backend already returned 404; we make the
    // cause obvious instead of silently using a fallback Space.
    return (
      <main className="loading-screen">
        <h1>Neznámá Space subdoména</h1>
        <p>
          Host <code>{window.location.host}</code> neodpovídá žádné aktivní Space v tomto tenantu.
        </p>
        <p>Zkontroluj dev subdoménu (sloupec <code>spaces.subdomain</code>) - viz dev runbook.</p>
      </main>
    );
  }
  if (status === 'anonymous') return <LoginScreen />;

  // Neutral host (login.*, www.*, api.* …): NOT a Space - route the user.
  // The canonical login host is login.<base>; after auth we redirect to the
  // active Space subdomain. Never silently fall back to Rondo.
  const hostname = window.location.hostname;
  const label = hostname.split('.')[0]!;
  const baseHost = hostname.split('.').slice(1).join('.');
  const portSuffix = window.location.port ? `:${window.location.port}` : '';
  const proto = window.location.protocol;
  if (isReservedHostLabel(label)) {
    const taskId = taskIdFromUrl(window.location.pathname, window.location.search);
    if (taskId != null && me) {
      return <TaskDeepLinkRedirect taskId={taskId} me={me} baseHost={baseHost} portSuffix={portSuffix} protocol={proto} />;
    }
    const activeSub = me?.activeSpaceSubdomain ?? null;
    if (activeSub && baseHost) {
      // Zachovej cílovou cestu (deep-link z MM notifikace, např. /ukoly?taskId=582),
      // ať se po přesměrování na Space host neztratí a úkol se přímo otevře.
      const dest = window.location.pathname === '/' ? '/' : `${window.location.pathname}${window.location.search}`;
      return <RedirectToSpaceHost url={`${proto}//${activeSub}.${baseHost}${portSuffix}${dest}`} />;
    }
    return <NoActiveSpace baseHost={baseHost} portSuffix={portSuffix} protocol={proto} />;
  }

  // Space host (or dev bare host) - AUTHORITATIVE: the backend-resolved active
  // Space decides the shell. A path can NOT override it on the wrong subdomain.
  const slug = me?.activeSpaceSlug ?? null;
  if (slug === 'affilmaster') return <AffilmasterShell />;
  if (slug === 'affilplatform') return <AffilplatformShell />;
  if (slug === 'dmwk') return <DmwkShell />;
  if (slug === 'rondo') return <AppShell />;
  // A real Space with no built shell yet → explicit placeholder, never Rondo.
  if (slug && !SHELLS_WITH_UI.has(slug)) {
    const taskId = taskIdFromUrl(window.location.pathname, window.location.search);
    if (taskId != null && me && baseHost) {
      return <TaskDeepLinkRedirect taskId={taskId} me={me} baseHost={baseHost} portSuffix={portSuffix} protocol={proto} />;
    }
    return <NotReadyShell slug={slug} />;
  }
  // Legacy `/affilmaster/*` path alias - DEV ONLY (local compat). Disabled in
  // production builds (import.meta.env.DEV === false), so e.g.
  // rondo.platform.odoi.co/affilmaster stays the Rondo shell.
  if (import.meta.env.DEV && window.location.pathname.startsWith('/affilmaster')) {
    return <AffilmasterShell />;
  }
  return <SpacesLanding />;
}

function RedirectToSpaceHost({ url }: { url: string }) {
  useEffect(() => {
    window.location.assign(url);
  }, [url]);
  return (
    <main className="loading-screen">
      <p>Přesměrování…</p>
    </main>
  );
}

function NotReadyShell({ slug }: { slug: string }) {
  return (
    <PlatformShell
      menu={[]}
      currentSpaceSlug={slug}
      activeModuleId="dashboard"
      isTasksActive={false}
      isHelpdeskActive={false}
      showTasks={false}
      showHelpdesk={false}
      onSelectModule={() => undefined}
      onSelectChild={() => undefined}
      onOpenTasks={() => undefined}
      onOpenTask={() => undefined}
      onOpenSettings={() => undefined}
    >
      <section className="prototype-page dmwk-not-ready">
        <header className="prototype-page-head">
          <div>
            <div className="prototype-eyebrow">{slug.toUpperCase()}</div>
            <h1>Tento Space se připravuje</h1>
            <p>Vlastní pracovní rozhraní právě dokončujeme.</p>
          </div>
        </header>
        <div className="prototype-panel prototype-empty-state">
          <div className="prototype-empty-icon" aria-hidden="true">◇</div>
          <h2>Brzy bude připravený</h2>
          <p>Mezitím můžete použít přepínač Spaces nebo nám poslat námět přes Bug report.</p>
        </div>
      </section>
    </PlatformShell>
  );
}
